import { useState, useEffect } from "react";
// Custom Hooks
import { useAuth } from "../../utils/firebase/AuthContext";
import { useNotification } from "../../utils/notification_context";
import { useLanguage } from "../../utils/language.js";
// Utils
import EditableGuestDetailItem from "./EditableGuestDetailItem";
import { USER_ROLE } from "../../utils/firebase/firebase";
// Components
import GuestDetailItem from "./GuestDetailItem";
import {
  isStringNullOrEmpty,
  validateMobile,
  validateNumber,
} from "../../utils/string_util.js";

function GuestDetailModal({ guest, onClose, getItemTotalPax, onUpdate }) {
  const { userRole } = useAuth();
  const { showErrorNotification } = useNotification();
  const { getTranslation } = useLanguage();

  const [isEditing, setIsEditing] = useState(false);
  const [editedGuest, setEditedGuest] = useState(guest);

  useEffect(() => {
    setEditedGuest(guest);
  }, [guest]);

  if (!editedGuest) return null;

  const handleIsEditingChange = () => {
    if (isEditing) {
      handleSave();
    } else {
      setIsEditing(true);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    let finalValue;

    switch (type) {
      case "boolean":
        finalValue = value === "true";
        break;
      case "number":
        finalValue = Number(value) || 0;
        break;
      case "option":
        if (name === "rsvp") {
          finalValue = value === "true";
        } else {
          finalValue = value;
          break;
        }
      default:
        // For 'string', 'option', etc., use the value as is.
        finalValue = value;
        break;
    }

    setEditedGuest((prev) => {
      const newState = {
        ...prev,
        [name]: finalValue,
      };

      // Keep the conditional logic to clear partnerName if partner is not attending
      if (name === "partnerAttending" && !finalValue) {
        newState.partnerName = "";
      }

      if (name === "childrenAttending" && !finalValue) {
        newState.childrenCount = 0;
        newState.babyChairCount = 0;
      }

      return newState;
    });
  };

  const handleSave = async () => {
    // Convert numbers back to numbers from string input
    if (validateForm()) {
      const finalData = {
        ...editedGuest,
        childrenCount: Number(editedGuest.childrenCount) || 0,
        babyChairCount: Number(editedGuest.babyChairCount) || 0,
      };

      if (userRole !== USER_ROLE.SUPER_ADMIN) {
        showErrorNotification(
          "You do not have enough permision to perform this action. Please ask the owner to grant you the access."
        );
        handleCancel();
        return;
      }

      await onUpdate(editedGuest.docId, finalData);
      setIsEditing(false); // Exit edit mode after saving
    } else {
      showErrorNotification(getTranslation("form_error_msg_already_exist"));
    }
  };

  const handleCancel = () => {
    setEditedGuest(guest); // Revert changes
    setIsEditing(false); // Exit edit mode
  };

  const validateForm = () => {
    if (editedGuest.rsvp === null) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_rsvp_choice"));
      return false;
    }

    if (isStringNullOrEmpty(editedGuest.guestName)) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_guest_name"));
      return false;
    }

    if (!validateMobile(editedGuest.guestContact)) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_guest_contact"));
      return false;
    }

    if (editedGuest.invitedBy === null) {
      showPrefixedErrorNotif(getTranslation("form_error_msg_invited_by"));
      return false;
    }

    if (editedGuest.rsvp === true) {
      if (editedGuest.partnerAttending === null) {
        showPrefixedErrorNotif(
          getTranslation("form_error_msg_partner_attendance")
        );
        return false;
      }

      if (editedGuest.partnerAttending) {
        if (isStringNullOrEmpty(editedGuest.partnerName)) {
          showPrefixedErrorNotif(getTranslation("form_error_msg_partner_name"));
          return false;
        }
      }

      if (editedGuest.invitedBy === "groom") {
        if (editedGuest.childrenAttending === null) {
          showPrefixedErrorNotif(
            getTranslation("form_error_msg_children_attendance")
          );
          return false;
        }

        if (editedGuest.childrenAttending) {
          if (
            editedGuest.childrenCount < 1 ||
            !validateNumber(editedGuest.childrenCount)
          ) {
            showPrefixedErrorNotif(
              getTranslation("form_error_msg_children_count")
            );
            return false;
          }

          if (!validateNumber(editedGuest.babyChairCount)) {
            showPrefixedErrorNotif(
              getTranslation("form_error_msg_children_baby_chair_count")
            );
            return false;
          }
        }
      }

      if (editedGuest.isVegetarian === null) {
        showPrefixedErrorNotif(
          getTranslation("form_error_msg_vegetarian_choice")
        );
        return false;
      }
    }

    return true;
  };

  const showPrefixedErrorNotif = (errorItem) => {
    showErrorNotification(
      `${getTranslation("form_error_msg_title")} ${errorItem}`
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#FDE8E4] rounded-2xl shadow-2xl p-8 max-w-lg w-full transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#931b1c]">
              {editedGuest.guestName}
            </h2>
            {isEditing ? (
              <EditableGuestDetailItem
                label=""
                name="rsvp"
                type="option"
                value={editedGuest.rsvp}
                onChange={handleInputChange}
                isEditing={isEditing}
                options={rsvpOptions}
              />
            ) : (
              <span
                className={`px-2 py-1 mt-1 inline-block text-xs font-semibold rounded-full ${
                  editedGuest.rsvp
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {editedGuest.rsvp ? "Accepted" : "Declined"}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-[#931b1c] text-3xl leading-none"
          >
            &times;
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <EditableGuestDetailItem
            label="Guest Contact"
            name="guestContact"
            type="string"
            value={editedGuest.guestContact}
            onChange={handleInputChange}
            isEditing={isEditing}
          />

          <EditableGuestDetailItem
            label="Name"
            name="guestName"
            type="string"
            value={editedGuest.guestName}
            onChange={handleInputChange}
            isEditing={isEditing}
          />
          <GuestDetailItem
            label="Total Pax"
            type="number"
            value={getItemTotalPax(editedGuest)}
            onChange={handleInputChange}
            isEditing={isEditing}
          />
          {isEditing ? (
            <EditableGuestDetailItem
              label="Partner Attending"
              name="partnerAttending"
              type="boolean"
              value={editedGuest.partnerAttending}
              onChange={handleInputChange}
              isEditing={isEditing}
            />
          ) : (
            <GuestDetailItem
              label="Partner Attending"
              value={
                editedGuest.partnerAttending
                  ? `Yes (${editedGuest.partnerName})`
                  : "No"
              }
              isEditing={isEditing}
            />
          )}

          {isEditing && editedGuest.partnerAttending && (
            <EditableGuestDetailItem
              label="Partner's Name"
              name="partnerName"
              type="string"
              value={editedGuest.partnerName}
              onChange={handleInputChange}
              isEditing={isEditing}
            />
          )}

          {isEditing ? (
            <EditableGuestDetailItem
              label="Children Attending"
              name="childrenAttending"
              type="boolean"
              value={editedGuest.childrenAttending}
              onChange={handleInputChange}
              isEditing={isEditing}
            />
          ) : (
            <GuestDetailItem
              label="Children Attending"
              value={
                editedGuest.childrenAttending
                  ? `Yes (${editedGuest.childrenCount}👶 ${
                      editedGuest.babyChairCount > 0
                        ? editedGuest.babyChairCount + "💺"
                        : ""
                    })`
                  : "No"
              }
              isEditing={isEditing}
            />
          )}

          {isEditing && editedGuest.childrenAttending && (
            <>
              <EditableGuestDetailItem
                label="Children's Count"
                name="childrenCount"
                type="number"
                value={editedGuest.childrenCount}
                onChange={handleInputChange}
                isEditing={isEditing}
              />

              <EditableGuestDetailItem
                label="Baby Chairs Needed"
                name="babyChairCount"
                type="number"
                value={editedGuest.babyChairCount}
                onChange={handleInputChange}
                isEditing={isEditing}
              />
            </>
          )}

          {isEditing ? (
            <EditableGuestDetailItem
              label="Is Vegetarian"
              name="isVegetarian"
              type="boolean"
              value={editedGuest.isVegetarian}
              onChange={handleInputChange}
              isEditing={isEditing}
            />
          ) : (
            <GuestDetailItem
              label="Is Vegetarian"
              value={editedGuest.isVegetarian ? "Yes" : "No"}
              isEditing={isEditing}
            />
          )}

          <EditableGuestDetailItem
            label="Invited By"
            name="invitedBy"
            type="option"
            value={editedGuest.invitedBy}
            onChange={handleInputChange}
            isEditing={isEditing}
            options={invitedByOptions}
          />

          <EditableGuestDetailItem
            label="Dietary Restrictions"
            name="dietaryRestrictions"
            value={editedGuest.dietaryRestrictions}
            onChange={handleInputChange}
            isEditing={isEditing}
            className="sm:col-span-2 w-full"
          />

          <div className="sm:col-span-2 flex flex-row justify-center items-center pt-2 border-t border-[#FFDDD7] mt-2">
            <GuestDetailItem
              label="Submitted At"
              value={editedGuest.createdAt.toDate().toLocaleString()}
              className="flex-1"
            />
            {userRole === USER_ROLE.SUPER_ADMIN && (
              <div className="self-end">
                <button
                  className=" bg-gray-300 hover:bg-gray-400 rounded-md shadow-sm px-4 py-2"
                  onClick={handleIsEditingChange}
                >
                  {isEditing ? "Save" : "Edit"}
                </button>
                {isEditing && (
                  <button className="ml-2" onClick={handleCancel}>
                    Cancel
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuestDetailModal;

const rsvpOptions = [
  { label: "Accepted", value: "true" },
  { label: "Declined", value: "false" },
];

const invitedByOptions = [
  { label: "Bride", value: "bride" },
  { label: "Groom", value: "groom" },
];
